/**
 * React hooks for lunch kiosk functionality.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiConfig } from '../config';

const API_BASE = apiConfig.baseUrl;

export interface LunchOrderItem {
  order_type: 'regular' | 'salad' | 'special';
  menu_item_id?: number;
  special_id?: number;
  topping_ids?: number[];
  option_ids?: number[];
}

export interface CreateLunchKioskOrderRequest {
  employee_name: string;
  employee_number: string | null;
  is_guest: boolean;
  orders: LunchOrderItem[];
  notes?: string;
  ticket_code?: string;
}

export interface LunchKioskOrderResponse {
  message: string;
  employee_name: string;
  order_count: number;
  order_ids: number[];
  order_date: string;
  ticket_redeemed?: boolean;
  ticket_code?: string;
}

async function createLunchKioskOrder(
  order: CreateLunchKioskOrderRequest
): Promise<LunchKioskOrderResponse> {
  const response = await fetch(`${API_BASE}/orders/kiosk`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(order),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to place lunch order');
  }

  return response.json();
}

export function usePlaceLunchKioskOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createLunchKioskOrder,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lunch', 'orders'] });
    },
  });
}
